/**
 * Acquisition Due Diligence — Report Template.
 *
 * Combines compliance assessment findings (from guided survey) with
 * 7 DD category summaries. Each category shows risk rating + items +
 * recommendations. Overall Go/Conditional/No-Go at top.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ACQUISITION_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_DESCRIPTIONS,
  RECOMMENDATION_META,
  getItemsByCategory,
  loadAcquisitionResponses,
  calculateCategorySummary,
  calculateOverallRecommendation,
  type AcquisitionCategory,
  type AcquisitionAssessmentResponse,
  type AcquisitionCategorySummary,
  type AcquisitionRecommendation,
} from '../../lib/services/acquisitionAssessment'
import { RATING_LABELS, type AssessmentRating } from '../../lib/services/dailyAssessment'

// ── Risk Badge ────────────────────────────────────────────────────────────

function RiskBadge({ level }: { level: 'low' | 'moderate' | 'high' }) {
  const styles = {
    low: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    moderate: 'bg-amber-100 text-amber-800 border-amber-300',
    high: 'bg-rose-100 text-rose-800 border-rose-300',
  }
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${styles[level]}`}>
      {level} risk
    </span>
  )
}

// ── Rating Chip ───────────────────────────────────────────────────────────

function RatingChip({ rating }: { rating: AssessmentRating }) {
  const colors = {
    1: 'bg-emerald-100 text-emerald-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-rose-100 text-rose-700',
  }
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${colors[rating]}`}>
      {RATING_LABELS[rating]}
    </span>
  )
}

// ── Category Report Section ───────────────────────────────────────────────

function CategoryReportSection({
  category,
  summary,
  responses,
}: {
  category: AcquisitionCategory
  summary: AcquisitionCategorySummary
  responses: Map<string, AcquisitionAssessmentResponse>
}) {
  const items = getItemsByCategory(category)
  const hasFindings = summary.red_count > 0 || summary.yellow_count > 0

  return (
    <section className="v11-section p-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{CATEGORY_LABELS[category]}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{CATEGORY_DESCRIPTIONS[category]}</p>
        </div>
        <RiskBadge level={summary.risk_level} />
      </div>

      {/* Stats bar */}
      <div className="mt-3 grid grid-cols-4 gap-2">
        <div className="rounded-lg bg-slate-50 p-2 text-center">
          <p className="text-lg font-bold text-slate-900">{summary.assessed_count}/{summary.item_count}</p>
          <p className="text-[10px] text-slate-500">Assessed</p>
        </div>
        <div className="rounded-lg bg-emerald-50 p-2 text-center">
          <p className="text-lg font-bold text-emerald-700">{summary.green_count}</p>
          <p className="text-[10px] text-emerald-600">Compliant</p>
        </div>
        <div className="rounded-lg bg-amber-50 p-2 text-center">
          <p className="text-lg font-bold text-amber-700">{summary.yellow_count}</p>
          <p className="text-[10px] text-amber-600">Concerns</p>
        </div>
        <div className="rounded-lg bg-rose-50 p-2 text-center">
          <p className="text-lg font-bold text-rose-700">{summary.red_count}</p>
          <p className="text-[10px] text-rose-600">Findings</p>
        </div>
      </div>

      {/* Score bar */}
      {summary.assessed_count > 0 && (
        <div className="mt-3">
          <div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
            {summary.green_count > 0 && (
              <div className="bg-emerald-500" style={{ width: `${(summary.green_count / summary.item_count) * 100}%` }} />
            )}
            {summary.yellow_count > 0 && (
              <div className="bg-amber-500" style={{ width: `${(summary.yellow_count / summary.item_count) * 100}%` }} />
            )}
            {summary.red_count > 0 && (
              <div className="bg-rose-500" style={{ width: `${(summary.red_count / summary.item_count) * 100}%` }} />
            )}
          </div>
          <p className="mt-1 text-right text-[10px] text-slate-400">
            Avg: {summary.average_rating.toFixed(2)} / 3.00
          </p>
        </div>
      )}

      {/* Findings detail */}
      {hasFindings && (
        <div className="mt-3 space-y-1.5">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Findings & Concerns</h4>
          {items
            .filter((item) => {
              const r = responses.get(item.item_id)
              return r && r.rating >= 2
            })
            .sort((a, b) => {
              const ra = responses.get(a.item_id)?.rating ?? 0
              const rb = responses.get(b.item_id)?.rating ?? 0
              return rb - ra // red first, then yellow
            })
            .map((item) => {
              const r = responses.get(item.item_id)!
              return (
                <div key={item.item_id} className="rounded-lg border border-slate-100 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                    <RatingChip rating={r.rating} />
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-500">{item.standard_ref}</p>
                  {r.note && (
                    <p className="mt-1 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                      {r.note}
                    </p>
                  )}
                </div>
              )
            })}
        </div>
      )}

      {/* Recommendation for category */}
      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Recommendation</h4>
        <p className="mt-1 text-xs text-slate-700">
          {summary.risk_level === 'high'
            ? `${CATEGORY_LABELS[category]} presents material risk. Detailed remediation plan required before close. ${summary.red_count} finding(s) require immediate attention.`
            : summary.risk_level === 'moderate'
              ? `${CATEGORY_LABELS[category]} has concerns that should be addressed as conditions of the transaction. ${summary.yellow_count} item(s) need further investigation.`
              : `${CATEGORY_LABELS[category]} assessment is satisfactory. Continue standard due diligence procedures.`}
        </p>
      </div>
    </section>
  )
}

// ── Main Report ───────────────────────────────────────────────────────────

export function AcquisitionReportPage() {
  const { visitId } = useParams<{ visitId: string }>()
  const [responses, setResponses] = useState<Map<string, AcquisitionAssessmentResponse>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!visitId) return
    const load = async () => {
      setLoading(true)
      loadAcquisitionResponses(visitId)
        .then((data) => {
          const map = new Map<string, AcquisitionAssessmentResponse>()
          for (const r of data) map.set(r.item_id, r)
          setResponses(map)
        })
        .catch((err) => setError((err as Error).message))
        .finally(() => setLoading(false))
    }
    load()
  }, [visitId])

  const categorySections = useMemo(() => {
    const arr = Array.from(responses.values())
    return ACQUISITION_CATEGORIES.map((cat) => ({
      category: cat,
      summary: calculateCategorySummary(cat, arr),
    }))
  }, [responses])

  const recommendation: AcquisitionRecommendation = useMemo(
    () => calculateOverallRecommendation(categorySections.map((s) => s.summary)),
    [categorySections],
  )
  const recMeta = RECOMMENDATION_META[recommendation]

  const totalAssessed = categorySections.reduce((n, s) => n + s.summary.assessed_count, 0)
  const totalItems = categorySections.reduce((n, s) => n + s.summary.item_count, 0)
  const totalRed = categorySections.reduce((n, s) => n + s.summary.red_count, 0)
  const totalYellow = categorySections.reduce((n, s) => n + s.summary.yellow_count, 0)
  const highRiskCategories = categorySections.filter((s) => s.summary.risk_level === 'high')

  if (!visitId) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Acquisition Due Diligence Report
        </h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-semibold">Unable to load acquisition report</p>
          <p className="text-sm">Visit not found</p>
          <Link className="mt-2 inline-block text-sm underline" to="/assessments/acquisition">
            Back to acquisition assessments
          </Link>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Acquisition Due Diligence Report
        </h1>
        <p className="p-8 text-center text-sm text-slate-500">
          Loading acquisition report...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Acquisition Due Diligence Report
        </h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-semibold">Unable to load acquisition report</p>
          <p className="text-sm">{error}</p>
          <Link className="mt-2 inline-block text-sm underline" to="/assessments/acquisition">
            Back to acquisition assessments
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 print:space-y-6">
      {/* Report Header */}
      <header className="v11-workspace-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
              Acquisition Due Diligence Report
            </h1>
            <p className="mt-1 text-xs text-slate-500">
              {totalAssessed}/{totalItems} items assessed across {ACQUISITION_CATEGORIES.length} categories
            </p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <Link
              to={`/assessments/acquisition/${visitId}`}
              className="text-xs font-semibold text-[var(--brand-primary)] underline"
            >
              Edit Assessment
            </Link>
            <Link
              to="/assessments/acquisition"
              className="text-xs font-semibold text-slate-500 underline"
            >
              Back
            </Link>
          </div>
        </div>

        {/* Overall Recommendation */}
        <div className={`mt-4 rounded-xl border-2 p-4 ${recMeta.color}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">
                Overall Recommendation
              </p>
              <p className="mt-1 text-2xl font-black">{recMeta.label}</p>
            </div>
            <div className="text-right text-xs opacity-80">
              <p>{totalRed} finding(s)</p>
              <p>{totalYellow} concern(s)</p>
              <p>{highRiskCategories.length} high-risk categor{highRiskCategories.length === 1 ? 'y' : 'ies'}</p>
            </div>
          </div>
          <p className="mt-2 text-sm">{recMeta.description}</p>
        </div>
      </header>

      {/* Executive Summary */}
      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Executive Summary</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {categorySections.map(({ category, summary }) => (
            <div key={category} className="v11-dashboard-card flex items-center justify-between px-3 py-2">
              <span className="text-xs font-bold text-slate-700">{CATEGORY_LABELS[category]}</span>
              <RiskBadge level={summary.risk_level} />
            </div>
          ))}
        </div>
      </section>

      {/* Category Detail Sections — high risk first */}
      {[...categorySections]
        .sort((a, b) => {
          const riskOrder = { high: 0, moderate: 1, low: 2 }
          return riskOrder[a.summary.risk_level] - riskOrder[b.summary.risk_level]
        })
        .map(({ category, summary }) => (
          <CategoryReportSection
            key={category}
            category={category}
            summary={summary}
            responses={responses}
          />
        ))}

      {/* Footer */}
      <footer className="v11-section p-4 text-center text-[10px] text-slate-400">
        Generated by HealthPro Platform | Acquisition Due Diligence Module
      </footer>
    </div>
  )
}
